import { apiRequest } from '../http';
import { ISettingsRepository } from './ISettingsRepository';
import { ParamUser, ParamActivity, CreateUserPayload, CurrentUserProfile, MyApplicationAccess, ApplicationMember, PersonLookup } from './types';
import { LccRole } from '../../auth/types';

export class RemoteSettingsRepository implements ISettingsRepository {
  constructor(private getToken: () => Promise<string | null>) {}

  async getMe(): Promise<CurrentUserProfile> {
    const token = await this.getToken();
    return apiRequest('/api/settings/me', { token });
  }

  async listMyApplications(): Promise<MyApplicationAccess[]> {
    const token = await this.getToken();
    return apiRequest('/api/settings/my-applications', { token });
  }

  async listUsers(includeInactive = false): Promise<ParamUser[]> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/users?includeInactive=${includeInactive}`, { token });
  }
  async createUser(payload: CreateUserPayload): Promise<ParamUser> {
    const token = await this.getToken();
    return apiRequest('/api/settings/users', { method: 'POST', body: payload, token });
  }
  async updateUser(id: string, patch: Partial<Pick<ParamUser, 'fullName' | 'isActive' | 'role' | 'allowedModules'>>): Promise<ParamUser> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/users/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteUser(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/settings/users/${id}`, { method: 'DELETE', token });
  }
  async resetUserPassword(id: string, password: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/settings/users/${id}/password`, { method: 'PUT', body: { password }, token });
  }
  async updateUserProfile(id: string, patch: { fullName?: string; email?: string }): Promise<ParamUser> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/users/${id}/profile`, { method: 'PUT', body: patch, token });
  }

  async listApplicationMembers(code: string, includeInactive = false): Promise<ApplicationMember[]> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/applications/${code}/members?includeInactive=${includeInactive}`, { token });
  }
  async lookupPersonByEmail(code: string, email: string): Promise<PersonLookup> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/applications/${code}/lookup?email=${encodeURIComponent(email)}`, { token });
  }
  async grantApplicationAccess(code: string, userId: string, role: LccRole, allowedModules?: string[] | null): Promise<ApplicationMember> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/applications/${code}/members/${userId}`, { method: 'PUT', body: { role, allowedModules }, token });
  }
  async revokeApplicationAccess(code: string, userId: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/settings/applications/${code}/members/${userId}`, { method: 'DELETE', token });
  }

  async listActivities(includeInactive = false): Promise<ParamActivity[]> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/activities?includeInactive=${includeInactive}`, { token });
  }
  async createActivity(name: string): Promise<ParamActivity> {
    const token = await this.getToken();
    return apiRequest('/api/settings/activities', { method: 'POST', body: { name }, token });
  }
  async updateActivity(id: string, patch: Partial<ParamActivity>): Promise<ParamActivity> {
    const token = await this.getToken();
    return apiRequest(`/api/settings/activities/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteActivity(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/settings/activities/${id}`, { method: 'DELETE', token });
  }
}
